import type { HandwritingProjectRecord } from '~~/layers/base/types/handwritingProject'
import type { StationeryPreviewImage } from '~~/layers/base/types/stationeryPreviewImage'
import {
    buildHandwrittenEnvelopeFacePreviewEntryFromDataUrl,
    buildHandwrittenHeroPreviewEntryFromDataUrl,
} from '~~/layers/base/utils/handwrittenHeroPreviewUpload'
import {
    isStationeryPreviewImage,
    isStationeryPreviewEnvelopeFace,
    resolveStationeryPreviewDeliveryUrl,
} from '~~/layers/base/utils/stationeryPreviewImage'

/** Discriminator written on entries in `handwriting_project.preview_image` (denormalized send-flow uploads). */
export const HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY = 'handwritten_denormalized_preview' as const

export type HandwrittenDenormalizedPreviewSlot = 'card_front' | 'envelope_face'

/**
 * Reads {@link HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY} from preview meta when present and valid.
 *
 * @returns `'card_front' | 'envelope_face'` or `null` when missing or unrecognized.
 */
function metaDenormalizedSlot(
    preview: StationeryPreviewImage
): HandwrittenDenormalizedPreviewSlot | null {
    const raw = preview.meta?.[HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY]
    return raw === 'card_front' || raw === 'envelope_face' ? raw : null
}

/**
 * Parses `surfaceId` / `surface_id` from stored meta (number or numeric string).
 * Used by legacy stationery matching when denormalized slot metadata is absent.
 */
function numericSurfaceIdFromMeta(meta: Record<string, unknown> | undefined): number | undefined {
    if (!meta) return undefined
    const raw = meta.surfaceId ?? meta.surface_id
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw
    if (typeof raw === 'string') {
        const n = Number(raw)
        return Number.isFinite(n) ? n : undefined
    }
    return undefined
}

/**
 * Lowercased trimmed part name from `partName` or snake_case `part_name` on meta.
 */
function partNameFromMeta(meta: Record<string, unknown> | undefined): string {
    if (!meta) return ''
    return String(meta.partName ?? meta.part_name ?? '')
        .toLowerCase()
        .trim()
}

/**
 * Normalize `handwriting_project.preview_image` JSONB for APIs and merge logic:
 * - `null` / missing → `[]`
 * - legacy single object → `[obj]` when valid
 * - array → validated entries only (order preserved but never relied upon for semantics)
 */
export function normalizeProjectPreviewImages(raw: unknown): StationeryPreviewImage[] {
    if (raw === null || raw === undefined) {
        return []
    }
    if (Array.isArray(raw)) {
        return raw.filter((x): x is StationeryPreviewImage => isStationeryPreviewImage(x))
    }
    if (isStationeryPreviewImage(raw)) {
        return [raw]
    }
    return []
}

/**
 * Persist shape for PATCH: `null` clears column; arrays are stored as JSON arrays (legacy single-objects
 * normalized to one-element arrays in the handler).
 */
export function persistProjectPreviewImageColumnValue(
    body: StationeryPreviewImage[] | StationeryPreviewImage | null
): StationeryPreviewImage[] | null {
    if (body === null) {
        return null
    }
    if (Array.isArray(body)) {
        return body.filter((x): x is StationeryPreviewImage => isStationeryPreviewImage(x))
    }
    return isStationeryPreviewImage(body) ? [body] : []
}

/**
 * Detects older hero / card-front preview rows stored without {@link HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY}.
 * Matches `label: hero`, front surface (`surfaceId` 1), or common stationery part tokens (card, note, folder).
 */
function looksLikeLegacyCardStockHeroPreview(preview: StationeryPreviewImage): boolean {
    const label = String(preview.label ?? '')
        .trim()
        .toLowerCase()
    if (label === 'hero') return true
    if (numericSurfaceIdFromMeta(preview.meta) !== 1) return false
    const part = partNameFromMeta(preview.meta)
    if (!part || part.includes('card') || part.includes('note') || part.includes('folder'))
        return true
    return false
}

/**
 * Upsert denormalized project preview array for send-flow uploads. Known slots replace prior rows that would
 * have matched that slot via {@link HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY} or legacy hero heuristics.
 */
export function mergeDenormalizedPreviewIntoArray(
    existing: StationeryPreviewImage[],
    entry: StationeryPreviewImage
): StationeryPreviewImage[] {
    const slot = metaDenormalizedSlot(entry)

    if (slot === 'envelope_face') {
        const next = existing.filter((p) => metaDenormalizedSlot(p) !== 'envelope_face')
        next.push(entry)
        return next
    }

    if (slot === 'card_front') {
        const next = existing
            .filter((p) => metaDenormalizedSlot(p) !== 'card_front')
            .filter((p) => !looksLikeLegacyCardStockHeroPreview(p))
        next.push(entry)
        return next
    }

    return [...existing, entry]
}

/**
 * Resolves the card-front / composed-note preview from a denormalized `preview_image` array.
 *
 * Prefer explicit `card_front` meta slot, then legacy `hero` label, then front-surface + part-name heuristics.
 *
 * **HOMS:** For `handwriting_project.preview_image` JSONB, pick the row with this helper, then
 * `readPaperSizeInchesFromPreviewMeta` + `resolveCardFrontSafeAreaInchesFromPreviewMeta` on `row.meta`.
 * For stationery catalog `preview_images`, use the same front-surface / card `partName` heuristics (or
 * `primary_preview_meta` fallback) — see `stationeryPreviewLayoutMeta.ts` module doc.
 *
 * @param previews - Normalized list (call {@link normalizeProjectPreviewImages} if reading JSONB raw).
 */
export function pickDenormalizedCardFrontPreview(
    previews: StationeryPreviewImage[] | null | undefined
): StationeryPreviewImage | null {
    if (!previews?.length) return null
    const bySlot = previews.find((p) => metaDenormalizedSlot(p) === 'card_front')
    if (bySlot) return bySlot

    const byHeroLabel = previews.find(
        (p) =>
            String(p.label ?? '')
                .trim()
                .toLowerCase() === 'hero'
    )
    if (byHeroLabel) return byHeroLabel

    const byLegacySurface = previews.find((p) => {
        if (numericSurfaceIdFromMeta(p.meta) !== 1) return false
        const part = partNameFromMeta(p.meta)
        if (!part) return true
        return (
            part.includes('card') ||
            part.includes('note') ||
            part.includes('notecard') ||
            part.includes('folder')
        )
    })
    return byLegacySurface ?? null
}

/**
 * Resolves envelope **Face** artwork from previews: explicit `envelope_face` slot first,
 * otherwise {@link isStationeryPreviewEnvelopeFace} among remaining entries.
 *
 * **HOMS:** Use on `handwriting_stationery.preview_images` or project `preview_image`, then
 * `resolveEnvelopeFaceSafeAreaInchesFromPreviewMeta` on the chosen row's `meta` (`stationeryPreviewLayoutMeta.ts`).
 */
export function pickDenormalizedEnvelopeFacePreview(
    previews: StationeryPreviewImage[] | null | undefined
): StationeryPreviewImage | null {
    if (!previews?.length) return null
    const bySlot = previews.find((p) => metaDenormalizedSlot(p) === 'envelope_face')
    if (bySlot) return bySlot
    return previews.find(isStationeryPreviewEnvelopeFace) ?? null
}

/**
 * Public HTTPS URL for the card-front Cloudflare image in `preview_image`, or null.
 * Normalizes the column then uses {@link pickDenormalizedCardFrontPreview} + {@link resolveStationeryPreviewDeliveryUrl}.
 */
/** Fabric snapshot + stationery meta held in the personalize session before / after Cloudflare upload. */
export type HandwrittenSessionPreviewSnapshot = {
    dataUrl: string
    meta: Record<string, unknown>
}

/**
 * Overlay in-session Fabric `data:` previews onto a project row for client store cache (Send → success).
 * Does not PATCH the server; data URLs are too large for `preview_image` persistence.
 */
export function applySessionDataUrlPreviewsToHandwritingProject(
    project: HandwritingProjectRecord,
    session: {
        cardFront?: HandwrittenSessionPreviewSnapshot | null
        envelopeFace?: HandwrittenSessionPreviewSnapshot | null
    }
): HandwritingProjectRecord {
    let previews = normalizeProjectPreviewImages(project.preview_image)

    const card = session.cardFront
    if (card?.dataUrl.startsWith('data:')) {
        previews = mergeDenormalizedPreviewIntoArray(
            previews,
            buildHandwrittenHeroPreviewEntryFromDataUrl(card.dataUrl, card.meta)
        )
    }

    const face = session.envelopeFace
    if (face?.dataUrl.startsWith('data:')) {
        previews = mergeDenormalizedPreviewIntoArray(
            previews,
            buildHandwrittenEnvelopeFacePreviewEntryFromDataUrl(face.dataUrl, face.meta)
        )
    }

    return { ...project, preview_image: previews }
}

/**
 * Display URL for denormalized send-flow previews: returns Fabric `data:` URLs as-is; otherwise
 * {@link resolveStationeryPreviewDeliveryUrl} (Cloudflare Images id or remote `external_url`).
 */
export function resolveHandwrittenPreviewDisplayUrl(
    preview: Pick<StationeryPreviewImage, 'cloudflare_image_id' | 'external_url'>,
    cloudflareImagesBaseUrl: string
): string | null {
    const ext = typeof preview.external_url === 'string' ? preview.external_url.trim() : ''
    if (ext.startsWith('data:')) {
        return ext
    }
    return resolveStationeryPreviewDeliveryUrl(preview, cloudflareImagesBaseUrl)
}

export function thumbnailUrlFromProjectDenormalizedPreviews(
    rawColumn: unknown,
    cloudflareBaseUrl: string
): string | null {
    const list = normalizeProjectPreviewImages(rawColumn)
    const pick = pickDenormalizedCardFrontPreview(list)
    if (!pick) return null
    return resolveStationeryPreviewDeliveryUrl(pick, cloudflareBaseUrl)
}

/**
 * Stationery-derived preview rows attached to a project row for APIs.
 *
 * Prefers **`stationery_preview_images`** when non-empty (canonical joined list); otherwise falls back to
 * **`preview_images`** (embedded / legacy payloads).
 */
export function resolvedStationeryPreviewImagesForProject(
    row:
        | {
              stationery_preview_images?: StationeryPreviewImage[] | null
              preview_images?: StationeryPreviewImage[] | null
          }
        | null
        | undefined
): StationeryPreviewImage[] {
    const fromCanonical = row?.stationery_preview_images
    if (Array.isArray(fromCanonical) && fromCanonical.length > 0) {
        return fromCanonical
    }
    return row?.preview_images ?? []
}
